import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Injector,
  NO_ERRORS_SCHEMA,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxUiLoaderModule } from 'ngx-ui-loader';
import { availableLanguages } from './core/constants/langConst';
import { HttpClientModule } from '@angular/common/http';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessagingService } from './services/messaging.service';
import { SwUpdateService } from './services/sw-update.service';
import { assets } from './core/constants/assets.const';
import { AudioService } from './services/audio.service';
import {
  isPublicHelpGuideRoute,
  shouldSuppressNotificationSounds,
  arePushNotificationsSupported,
} from './core/utils/public-route.util';
import {
  safeSessionStorageGetItem,
  safeSessionStorageSetItem,
} from './core/utils/safe-storage.util';

const PUSH_UNAVAILABLE_ALERT_SESSION_KEY =
  'logibids_push_unavailable_alert_shown';

const PUSH_UNAVAILABLE_ALERT_IOS =
  'Push alerts cannot work on iPhone because Apple does not allow websites to send live notifications through Safari or Chrome on iPhone. This is an Apple restriction, not a problem with Logibids Admin. You can still use the admin panel normally here. For instant alerts, please open the admin panel on a computer or Android phone.';

const PUSH_UNAVAILABLE_ALERT_GENERIC =
  'This browser does not support live push alerts from websites. You can still use Logibids Admin normally. For instant alerts, please open the admin panel on a computer or Android phone.';
@Component({
  selector: 'logibids-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    NgxUiLoaderModule,
    TranslateModule,
    HttpClientModule,
    ToastModule,
    ConfirmDialogModule,
  ],
  providers: [TranslateService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppComponent implements OnInit {
  title = 'logibids_admin';
  availableLanguages = availableLanguages;
  private injector = inject(Injector);
  private AudioService = inject(AudioService);
  private assetsConst = assets;
  private browserInitDone = false;
  private pushUnavailableAlertShownInMemory = false;
  constructor(
    public translate: TranslateService,
    private router: Router
  ) {
    this.AudioService.register(
      'notification',
      this.assetsConst.NotificationSound
    );
    this.translate.addLangs(this.availableLanguages);
    this.translate.use(this.availableLanguages[0]);
    this.translate.setDefaultLang(this.availableLanguages[0]);

    if (!isPublicHelpGuideRoute()) {
      this.initBrowserOnly();
      this.browserInitDone = true;
    }
  }

  ngOnInit(): void {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (
          !this.browserInitDone &&
          !shouldSuppressNotificationSounds(event.urlAfterRedirects)
        ) {
          this.initBrowserOnly();
          this.browserInitDone = true;
        }

        document.body.scrollTop = 0;
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 0);
      }
    });
  }

  private initBrowserOnly(): void {
    const swUpdateService = this.injector.get(SwUpdateService);
    swUpdateService.checkForUpdates();

    if (!arePushNotificationsSupported()) {
      this.maybeShowPushUnavailableAlert();
      return;
    }

    const messaging = this.injector.get(MessagingService);
    messaging.requestPermission();
    messaging.listenForFrontMessages();

    const message = this.translate.instant('notificationAlert');

    if (Notification.permission === 'denied') {
      alert(message);
    }

    navigator.serviceWorker?.addEventListener(
      'message',
      e => this.handleSWMessage(e),
      {
        passive: true,
      }
    );
  }

  private maybeShowPushUnavailableAlert(): void {
    if (this.pushUnavailableAlertShownInMemory) {
      return;
    }

    if (safeSessionStorageGetItem(PUSH_UNAVAILABLE_ALERT_SESSION_KEY) === '1') {
      this.pushUnavailableAlertShownInMemory = true;
      return;
    }

    this.pushUnavailableAlertShownInMemory = true;
    safeSessionStorageSetItem(PUSH_UNAVAILABLE_ALERT_SESSION_KEY, '1');

    const message = this.isIosBrowser()
      ? PUSH_UNAVAILABLE_ALERT_IOS
      : PUSH_UNAVAILABLE_ALERT_GENERIC;
    window.setTimeout(() => alert(message), 400);
  }

  private isIosBrowser(): boolean {
    if (typeof navigator === 'undefined') {
      return false;
    }

    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  private handleSWMessage(event: MessageEvent): void {
    const { action, payload } = event.data ?? {};

    switch (action) {
      case 'navigate': {
        const url = payload?.url;
        if (url) {
          try {
            const urlObj = new URL(url);
            const relativeUrl = urlObj.pathname + urlObj.search + urlObj.hash;
            this.router.navigateByUrl(relativeUrl);
          } catch (e) {
            console.error('Failed to parse navigation URL:', url);
            this.router.navigateByUrl(url); // Fallback
          }
        }
        payload?.notifications?.length &&
          this.injector
            .get(MessagingService)
            .sendMessage(payload.notifications);
        break;
      }

      case 'playNotificationSound':
        if (!shouldSuppressNotificationSounds()) {
          this.playNotification();
        }
        break;
    }
  }

  private playNotification(): void {
    this.AudioService.play('notification');
  }
}
